import { SkedPreview } from '@dialogueconsulting/sked-preview'
import isEmpty from 'lodash.isempty'
import startcase from 'lodash.startcase'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { fetchPostSkedPreview, fetchGridPreview, fetchSetting } from 'api'
import { PostPreviewGrid } from 'Components'
import {
  handleStatusStateBg,
  handleStatusStateColor,
} from 'Components/UpcomingPostCard'
import GlobalStyle from 'GlobalStyle'
import useKeyPress from 'hooks/useKeyPress'
import { ArrowLeftIcon, ArrowRightIcon } from 'Icons'
import {
  normalizeSkedPreviewPost,
  normalizeGridPreview,
  isPostIsStory,
} from 'normalizer'
import type { Platform } from 'Types'

import PlatformIcon from './Common/PlatformIcon'

export interface SelectedTile {
  id: string
  date: string
  time: string
  platform: Platform
  status: string
}

export interface CalendarModalProps {
  reviewKey: string
  selectedTile: SelectedTile
  reviewPostClick: () => void
  navigationArrowClick: (
    direction: 'left' | 'right',
    currentId: string
  ) => () => void
  hideArrowLeft: boolean
  hideArrowRight: boolean
  isReadOnly: boolean
}

const CalendarModal: React.FC<CalendarModalProps> = ({
  reviewKey,
  selectedTile,
  reviewPostClick,
  navigationArrowClick,
  hideArrowLeft,
  hideArrowRight,
  isReadOnly,
}) => {
  const [isLoadingSkedPreview, setIsLoadingSkedPreview] = useState(false)
  const [skedPreviewPost, setSkedPreviewPost] = useState<any>([])

  const [isLoadingGridData, setIsLoadingGridData] = useState<any>(undefined)
  const [gridData, setGridData] = useState<any>(undefined)

  useKeyPress('ArrowLeft', () => {
    const ArrowLeftElement = document.querySelector(
      '[data-testid="arrow-left"]'
    ) as HTMLElement | null

    ArrowLeftElement?.click()
  })
  useKeyPress('ArrowRight', () => {
    const ArrowRightElement = document.querySelector(
      '[data-testid="arrow-right"]'
    ) as HTMLElement | null

    ArrowRightElement?.click()
  })

  useEffect(() => {
    const fetchPostSkedPreviewAsync = async (
      reviewKey: string,
      postId: string
    ) => {
      try {
        setIsLoadingSkedPreview(true)
        const skedPreviewResponse = await fetchPostSkedPreview(
          reviewKey,
          postId
        )
        const setting = await fetchSetting(reviewKey)

        const normalizedSkedPreviewPost = normalizeSkedPreviewPost(
          skedPreviewResponse,
          setting.accounts
        )

        setSkedPreviewPost(normalizedSkedPreviewPost)
        setIsLoadingSkedPreview(false)

        if (
          skedPreviewResponse &&
          skedPreviewResponse.platform === 'IG' &&
          skedPreviewResponse.post_stage !== 'draft' &&
          !isPostIsStory(skedPreviewResponse)
        ) {
          setIsLoadingGridData(true)
          const gridPreviewResponse = await fetchGridPreview(reviewKey, postId)

          if (gridPreviewResponse) {
            const normalizedGridPreviewPost = normalizeGridPreview(
              gridPreviewResponse,
              postId
            )
            setGridData(normalizedGridPreviewPost)
          } else {
            setGridData(undefined)
          }
          setIsLoadingGridData(false)
        } else {
          setGridData(undefined)
        }
      } catch {
        // this appears to be how "empty state" is handled here
        setGridData(undefined)
      }
    }

    fetchPostSkedPreviewAsync(reviewKey, selectedTile.id)
  }, [selectedTile.id])

  const emptyGridData = isEmpty(gridData)

  return (
    <Wrap isSingleCol={emptyGridData}>
      <Badge status={selectedTile.status} isReadOnly={isReadOnly}>
        <PlatformIcon platform={selectedTile.platform} />
        {isReadOnly
          ? startcase(selectedTile.platform)
          : startcase(selectedTile.status)}
        {selectedTile.status !== 'requestChange' && (
          <>
            <span>•</span>
            {selectedTile.time}, {selectedTile.date}
          </>
        )}
      </Badge>
      <GlobalStyle />
      {isLoadingSkedPreview || isLoadingGridData || isEmpty(skedPreviewPost) ? (
        <LoaderWrap>Loading...</LoaderWrap>
      ) : (
        <>
          <Col isSingleCol={emptyGridData}>
            <div>
              <SkedPreviewStyled
                post={skedPreviewPost}
                className={undefined}
                env={undefined}
                trackEvent={undefined}
                videoCoverPreview={skedPreviewPost?.providedPost?.thumbnailUrl}
              />
            </div>
            {!emptyGridData && (
              <PostPreviewGridWrap>
                <PostPreviewGrid {...gridData} isReadOnly={isReadOnly} />
              </PostPreviewGridWrap>
            )}
          </Col>

          {!isReadOnly && (
            <StickyRow>
              <ReviewPostBtn
                onClick={reviewPostClick}
                isSingleCol={emptyGridData}
              >
                Review Post
              </ReviewPostBtn>
            </StickyRow>
          )}

          {!hideArrowLeft && (
            <ArrowIconWrap
              direction="left"
              data-testid="arrow-left"
              onClick={navigationArrowClick('left', selectedTile.id)}
            >
              <ArrowLeftIcon />
            </ArrowIconWrap>
          )}
          {!hideArrowRight && (
            <ArrowIconWrap
              direction="right"
              data-testid="arrow-right"
              onClick={navigationArrowClick('right', selectedTile.id)}
            >
              <ArrowRightIcon />
            </ArrowIconWrap>
          )}
        </>
      )}
    </Wrap>
  )
}

export default CalendarModal

const Wrap = styled.div<{ isSingleCol: boolean }>`
  height: 92vh;
  margin: 0 auto;
  overflow-y: auto;
  padding: ${({ isSingleCol }) => (isSingleCol ? '0 60px' : '0 60px 0 50px')};
`

const Col = styled.div<{ isSingleCol: boolean }>`
  display: flex;
  justify-content: ${({ isSingleCol }) =>
    isSingleCol ? 'center' : 'space-evenly'};
  margin-top: 64px;
  padding-bottom: 50px;
`

const PostPreviewGridWrap = styled.div`
  position: relative;
  height: 510px;

  .container {
    padding-top: 64px;
    padding-left: 54px;

    .grid-container {
      max-height: 386px;
      overflow: auto;

      .first-grid-item {
        border: none;
      }
    }
  }
`

const ReviewPostBtn = styled.button<{ isSingleCol: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  color: #ffffff;
  background: #4f31ff;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 700;
  width: 240px;
  cursor: pointer;
  margin: 30px auto;
  padding: 10px 0;
`

const LoaderWrap = styled.div`
  position: absolute;
  top: 50%;
  font-size: 16px;
  left: 50%;
  z-index: 2;
  transform: translate(-50%, -50%);
`

const Badge = styled.div<{ status: string; isReadOnly: boolean }>`
  background: ${({ status, isReadOnly }) =>
    handleStatusStateBg(status, isReadOnly)};
  color: ${({ status, isReadOnly }) =>
    isReadOnly ? '#ffffff' : handleStatusStateColor(status)};
  height: 50px;
  position: absolute;
  z-index: 999;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  padding-right: 16px;
  font-size: 16px;
  font-weight: 700;
  border-top-left-radius: 10px;
  border-bottom-right-radius: 10px;

  span {
    padding: 0 4px;
  }

  svg {
    margin: 16px 13px 16px 16px;
    width: 23px;
    height: 23px;
  }
`

const StickyRow = styled.div`
  z-index: 3;
  position: absolute;
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  bottom: 0;
`

const ArrowIconWrap = styled.div<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  width: 40px;
  ${({ direction }) => (direction === 'left' ? 'left: 10px' : 'right: 10px;')};

  svg {
    cursor: pointer;
  }
`
const SkedPreviewStyled = styled(SkedPreview)`
  background: red;
  width: 360px;
  overflow: auto;
  border-radius: 10px;
  box-shadow: 3px 0 10px rgb(0 0 0 / 15%);
  background: #ffffff;
`
